import { Controller, FormProvider, useForm } from 'react-hook-form';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Message } from 'semantic-ui-react';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { LabeledSelect } from 'components/form-elements/selects/LabeledSelect';
import { Brikk } from 'components/Brikk';
import { ShouldRender } from 'components/ShouldRender';
import { useCreateAccessRule, useGetAccessRules } from 'services/2n/access-rules';
import { useGetGroups } from 'services/2n/groups';
import { useGetTimeProfiles } from 'services/2n/time-profiles';
import { useGetZones } from 'services/2n/zones';
import { useAccessManagement2NModal } from '../../../AccessManagement2NModalContext/useAccessManagement2NModal';
import styles from './styles.module.scss';
import { FormActions } from './components/FormActions';

const buildAccessRuleName = (groupOption, zoneOption) => [
  groupOption && groupOption.label,
  zoneOption && zoneOption.label,
].filter(Boolean).join(' - ');

export function AddAccessRuleForm() {
  const accessManagement2NModal = useAccessManagement2NModal();
  const { createAccessRuleAsync, isLoading: isCreating } = useCreateAccessRule();
  const { refetchAccessRules } = useGetAccessRules();
  const { groups } = useGetGroups();
  const { timeProfiles } = useGetTimeProfiles();
  const { zones } = useGetZones();

  const timeProfileOptions = (timeProfiles || []).map((timeProfile) => ({
    value: timeProfile.Id,
    label: timeProfile.Name,
  }));

  const zoneOptions = (zones || []).map((zone) => ({
    value: zone.Id,
    label: zone.Name,
  }));

  const groupsInThisRule = (groups || []).map((group) => ({
    value: group.Id,
    label: group.Name,
  }));

  const formMethods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      timeProfile: null,
      zone: null,
      group: null,
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors } } = formMethods;
  
  const onSubmit = async (data) => {
    const generatedName = buildAccessRuleName(data.group, data.zone);

    await createAccessRuleAsync({
      name: data.name || generatedName,
      timeProfileId: data.timeProfile && data.timeProfile.value,
      zoneId: data.zone && data.zone.value,
      groupId: data.group && data.group.value,
    }, {
      onSuccess: (accessRule) => {
        accessManagement2NModal.updateSelected('accessRule', accessRule);
        accessManagement2NModal.updateAdding('accessRule', false);
      },
    });

    if (refetchAccessRules) {
      refetchAccessRules();
    }

  };

  useEffect(() => {
    if (formMethods.formState.isDirty) {
      accessManagement2NModal.updateSavedChanges('accessRule', formMethods.formState.isDirty);
    }
  }, [formMethods.formState.isDirty]);

  return (
    <div className={styles.formContainer}>
      <FormProvider {...formMethods}>
        <Brikk.Heading heading="addNewAccessRule" />

        <Form
          id="add-access-rule-form"
          className={styles.form}
          onSubmit={handleSubmit(onSubmit)}
        >
        
          <ShouldRender condition={!!errors.name}>
            <Message negative>
              <Message.Content>
                <FormattedMessage
                  id="accessRuleNameRequired"
                  defaultMessage="accessRuleNameRequired"
                />
              </Message.Content>
            </Message>
          </ShouldRender>

          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field: { ref, onChange, value } }) => (
              <LabeledInput
                labelIntl="accessRuleName"
                id="access-rule-name"
                value={value}
                inputRef={ref}
                onChange={onChange}
                hasError={!!errors.name}
                disabled
              />
            )}
          />

          <Controller
            name="timeProfile"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <LabeledSelect
                title="timeProfile"
                options={timeProfileOptions}
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors.timeProfile}
              />
            )}
          />

          <Controller
            name="zone"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <LabeledSelect
                title="resourceName"
                options={zoneOptions}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  setValue('name', buildAccessRuleName(getValues('group'), value), {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                hasError={!!errors.zone}
              />
            )}
          />

          <Controller
            name="group"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <LabeledSelect
                title="groupName"
                options={groupsInThisRule}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  setValue('name', buildAccessRuleName(value, getValues('zone')), {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                hasError={!!errors.group}
              />
            )}
          />
        </Form>
        <FormActions
          isLoading={isCreating}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </div>
  );
}
